import type { ColumnDef } from "@tanstack/react-table";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { MqttApi } from "~/api/mqtt-api";
import { ComboBox } from "~/components/common/combo-box";
import { ContentContainer } from "~/components/common/content-container";
import { SimpleTable } from "~/components/common/simple-table";
import { CodeMirrorDialogFormField } from "~/components/form/custom/code-mirror-dialog-form-field";
import BrokerSelecter from "~/components/mqtt/broker-select";
import { Button, Input, Label } from "~/components/ui";
import type {
  MqttBroker,
  MqttMetaDto,
  MqttParsingType,
  MqttTopicDecodeTestDto,
  MqttTopicDto,
} from "~/types/api";
import type { FormOption } from "~/types/ui";
import { deafaultMqttTopic, mqttTopicDecoders } from "~/utils/mqtt-util";
import { defaultTableColumns } from "~/utils/table-util";
import { JsonDisplay } from "~/components/event-display/json-display";

export default function MqttFormField({
  value,
  onChange,
  options = [],
  publish = false,
  subscribe = false,
}: {
  value: MqttMetaDto;
  onChange: (val: MqttMetaDto) => void;
  options?: { label: string; value: MqttBroker }[];
  publish?: Boolean;
  subscribe?: Boolean;
}) {
  const { t } = useTranslation();

  return (
    <div className="ml-3">
      <div className="mb-2">
        <Label className="text-sm">{t("mqtt.topics.fields.broker")}</Label>
        <BrokerSelecter
          value={value?.brokerId ?? ""}
          onChange={(val) => {
            onChange({ ...value, brokerId: val });
          }}
          options={options.map((x) => x.value)}
          canCreate
        />
      </div>
      {publish && (
        <div className="mb-2">
          <Label className="text-sm">
            {t("mqtt.topics.fields.publishTopic")}
          </Label>
          <Input
            value={value.publishTopic ?? ""}
            placeholder={t("mqtt.topics.placeholders.publishTopic")}
            onChange={(e) =>
              onChange({ ...value, publishTopic: e.target.value })
            }
          />
        </div>
      )}
      {subscribe && (
        <div>
          <Label className="text-sm">{t("mqtt.topics.subscribe")}</Label>
          <MqttTopicSubscribe
            value={value.topics ?? []}
            onChange={(val) => {
              onChange({ ...value, topics: val });
            }}
          />
        </div>
      )}
    </div>
  );
}

export function MqttTopicSubscribe({
  value,
  onChange,
}: {
  value: MqttTopicDto[];
  onChange: (val: MqttTopicDto[]) => void;
}) {
  const { t } = useTranslation();
  const [selectedTopic, setSelectedTopic] =
    useState<MqttTopicDto>(deafaultMqttTopic);

  async function handleAdd(topic: MqttTopicDto) {
    const idx = value.findIndex((x) => x.mqttTopicId == topic.mqttTopicId);
    const nextTopic =
      topic.mqttTopicId != undefined && idx != -1
        ? topic
        : { ...topic, mqttTopicId: crypto.randomUUID() };

    setSelectedTopic(deafaultMqttTopic);
    onChange(idx != -1 ? value.with(idx, nextTopic) : value.concat(nextTopic));
  }

  async function handleEdit(topics: MqttTopicDto[]) {
    if (topics.length > 1) {
      toast.error(
        t("common.errors.expectedSingleEditData", {
          resource: t("mqtt.topics.fields.subscribeTopic"),
        }),
      );
      console.error(
        "Error: Can only edit 1 MQTT Topic at a time. Will edit the first one",
      );
    }
    toast.success(
      t("common.editResourceAddSection", {
        resource: topics.at(0)?.topic ?? t("mqtt.topics.fields.subscribeTopic"),
      }),
    );
    setSelectedTopic(topics.at(0) ?? deafaultMqttTopic);
  }

  async function handleRemove(topics: MqttTopicDto[]) {
    const removingKeys = topics.map((x) => x.mqttTopicId);
    const modifiedValue = value.filter(
      (x) => !removingKeys.includes(x.mqttTopicId),
    );
    onChange(modifiedValue);
  }

  const colDef: ColumnDef<any>[] = [
    defaultTableColumns["select"],
    {
      id: "topic",
      accessorKey: "topic",
      header: t("mqtt.topics.fields.subscribeTopic"),
      cell: (info) => info.getValue(),
    },
  ];

  return (
    <>
      <MqttTopicCreate topic={selectedTopic} onAdd={handleAdd} />
      <SimpleTable
        data={value}
        colDef={colDef}
        pageSizes={[5, 10, 25]}
        onEdit={handleEdit}
        onRemove={handleRemove}
        singleEditable
        removable
      />
    </>
  );
}

export function MqttTopicCreate({
  topic,
  onAdd,
}: {
  topic?: MqttTopicDto;
  onAdd: (val: MqttTopicDto) => void;
}) {
  const { t } = useTranslation();
  const [localTopic, setLocalTopic] = useState<MqttTopicDto>(
    topic ?? deafaultMqttTopic,
  );

  const [localTestValues, setLocalTestValues] = useState<{
    topic: string;
    payload: string;
  }>({ topic: "", payload: "" });

  const [testResult, setTestResult] = useState<string | undefined>();

  useEffect(() => {
    if (topic) {
      setLocalTopic(topic);
    }
  }, [topic]);

  async function handleAdd() {
    if (localTopic.topic && localTopic.decoder) {
      onAdd(localTopic);
      setLocalTopic(deafaultMqttTopic);
      return;
    }
    toast.error(t("common.errors.missingFields"));
  }

  async function handleTest() {
    if (
      !localTopic.decoder ||
      localTopic.parsingType == undefined ||
      !localTestValues.topic ||
      !localTestValues.payload
    ) {
      toast.error(t("common.errors.missingFields"));
      return;
    }

    const result = await new MqttApi().testDecoder({
      topic: localTestValues.topic,
      payload: localTestValues.payload,
      decoder: localTopic.decoder,
      parsingType: localTopic.parsingType,
    } as MqttTopicDecodeTestDto);

    if (!result) {
      toast.error(t("common.errors.general"));
      return;
    }
    result.ok
      ? setTestResult(JSON.stringify(result.data ?? []))
      : toast.error((result.error ?? "Unkown error") as string);
  }

  return (
    <ContentContainer>
      <div className="flex">
        <div className="mr-2 w-1/4">
          <Label className="text-sm">
            {t("mqtt.topics.fields.parsingType")}
          </Label>
          <ComboBox
            value={localTopic.parsingType}
            placeholder={t("mqtt.topics.placeholders.parsingType")}
            options={
              mqttTopicDecoders.map((x) => {
                return { label: t(`mqtt.topics.decoders.${x}`), value: x };
              }) as unknown as FormOption<number>[]
            }
            onChange={(val) => {
              setLocalTopic({
                ...localTopic,
                parsingType: val as MqttParsingType,
              });
            }}
          />
        </div>

        <div className="mr-10 w-1/4">
          <Label className="text-sm">
            {t("mqtt.topics.fields.subscribeTopic")}
          </Label>
          <Input
            value={localTopic.topic ?? ""}
            placeholder={t("mqtt.topics.placeholders.subscribeTopic")}
            onChange={(e) =>
              setLocalTopic({
                ...localTopic,
                topic: e.target.value,
              })
            }
          />
        </div>

        <div className="mr-2 w-1/4">
          <Label className="text-sm">{t("mqtt.topics.fields.testTopic")}</Label>
          <Input
            value={localTestValues.topic ?? ""}
            placeholder={t("mqtt.topics.placeholders.testTopic")}
            onChange={(e) =>
              setLocalTestValues({
                ...localTestValues,
                topic: e.target.value,
              })
            }
          />
        </div>
        <div className="mr-2 w-1/4">
          <Label className="text-sm">
            {t("mqtt.topics.fields.testPayload")}
          </Label>
          <Input
            value={localTestValues.payload ?? ""}
            placeholder={t("mqtt.topics.placeholders.testPayload")}
            onChange={(e) =>
              setLocalTestValues({
                ...localTestValues,
                payload: e.target.value,
              })
            }
          />
        </div>
      </div>

      <div className="mt-1 flex justify-between mr-2">
        <CodeMirrorDialogFormField
          value={localTopic.decoder ?? deafaultMqttTopic.decoder ?? ""}
          onChange={(val) => setLocalTopic({ ...localTopic, decoder: val })}
          dialogButtonText="mqtt.topics.fields.decoder"
        />
        <div className="">
          <Button
            variant="outline"
            aria-label="Test Selected"
            onClick={handleTest}
            type="button"
          >
            <Label className="text-sm">{t("common.test")}</Label>
          </Button>
        </div>
      </div>

      {testResult && (
        <div className="mt-3">
          <Label className="text-sm">{t("common.testResult")}</Label>
          <JsonDisplay hideCopy value={testResult} useCodeMirrorDisplay />
        </div>
      )}

      <div className="mt-2">
        <Button
          variant="outline"
          aria-label="Add Selected"
          onClick={handleAdd}
          type="button"
        >
          <Label className="text-sm">
            {localTopic.mqttTopicId ? t("common.edit") : t("common.add")}
          </Label>
        </Button>
      </div>
    </ContentContainer>
  );
}
